import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useBranches } from "@/hooks/useBranches";
import { useProfits } from "@/hooks/useProfits";
import { formatMoney, formatQty } from "@/lib/format";
import { ProfitTrendChart, ProfitTopProductsChart } from "@/pages/admin/profits/components/ProfitCharts";
import type { ProfitPeriod } from "@/types";

const PERIODS: { value: ProfitPeriod; label: string; hint: string }[] = [
  { value: "day", label: "Día", hint: "Últimos 30 días" },
  { value: "week", label: "Semana", hint: "Últimas 12 semanas" },
  { value: "month", label: "Mes", hint: "Últimos 12 meses" },
  { value: "year", label: "Año", hint: "Últimos 5 años" },
];

export default function AdminProfitsPage() {
  const { profile, isAdmin, hasPermission } = useAuth();
  const { branches } = useBranches();

  const activeBranches = useMemo(() => branches.filter((b) => b.active), [branches]);
  const [branchId, setBranchId] = useState<string | null>(null);
  const [period, setPeriod] = useState<ProfitPeriod>("day");

  useEffect(() => {
    if (profile?.branch_id) setBranchId(profile.branch_id);
  }, [profile?.branch_id]);

  const { summary, loading, error, reload } = useProfits(period, branchId);

  const canView = isAdmin || hasPermission("profit.view") || hasPermission("cash.view");
  const periodMeta = PERIODS.find((p) => p.value === period) ?? PERIODS[0];

  const statCards = [
    {
      key: "revenue",
      label: "Ingresos",
      value: formatMoney(summary?.revenue ?? 0),
      hint: `${summary?.salesCount ?? 0} venta(s)`,
      icon: "ri-money-dollar-circle-line",
      tone: "primary",
    },
    {
      key: "cost",
      label: "Costo de mercancía",
      value: formatMoney(summary?.cost ?? 0),
      hint: "Precio de compra",
      icon: "ri-shopping-bag-3-line",
      tone: "secondary",
    },
    {
      key: "profit",
      label: "Ganancia neta",
      value: formatMoney(summary?.profit ?? 0),
      hint: "Venta − compra",
      icon: "ri-funds-line",
      tone: "accent",
    },
    {
      key: "margin",
      label: "Margen",
      value: `${(summary?.margin ?? 0).toFixed(1)}%`,
      hint: `Ticket prom. ${formatMoney(summary?.avgTicket ?? 0)}`,
      icon: "ri-percent-line",
      tone: "accent",
    },
  ];

  const toneClass: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-800",
    secondary: "bg-secondary-100 text-secondary-800",
  };

  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-background-200 bg-background-100 px-4 py-16 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-lock-2-line text-2xl"></i>
        </span>
        <p className="text-sm font-semibold text-foreground-800">Acceso restringido</p>
        <p className="max-w-sm text-xs text-foreground-500">
          Tu usuario no tiene permiso para ver las ganancias.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">Ganancias y Reportes</h1>
          <p className="mt-1.5 text-sm text-foreground-500">
            Ganancia real por producto y período (precio de venta − precio de compra). {periodMeta.hint}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {activeBranches.length > 1 ? (
            <select
              value={branchId ?? ""}
              onChange={(event) => setBranchId(event.target.value || null)}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm font-semibold text-foreground-800 outline-none focus:border-primary-400"
            >
              <option value="">Todas las sucursales</option>
              {activeBranches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            onClick={reload}
            className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            <i className="ri-refresh-line text-base"></i>
            Actualizar
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1 rounded-full bg-background-200 p-1">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => setPeriod(p.value)}
            className={`flex flex-1 items-center justify-center whitespace-nowrap rounded-full px-4 py-2 text-xs font-semibold transition-colors sm:flex-none ${
              period === p.value ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {statCards.map((card) => (
          <div
            key={card.key}
            className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-100 p-4"
          >
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md ${toneClass[card.tone]}`}>
              <i className={`${card.icon} text-lg`}></i>
            </span>
            <div className="min-w-0">
              <p className="text-xs font-medium text-foreground-500">{card.label}</p>
              <p className="font-heading mt-0.5 truncate text-lg font-bold text-foreground-950">
                {loading ? "—" : card.value}
              </p>
              <p className="mt-0.5 truncate text-[11px] text-foreground-400">{card.hint}</p>
            </div>
          </div>
        ))}
      </div>

      {error ? (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-2">
            <i className="ri-error-warning-line text-base"></i>
            {error}
          </span>
          <button
            type="button"
            onClick={reload}
            className="whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-primary-700"
          >
            Reintentar
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-sm text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-lg"></i>
          Calculando ganancias...
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center justify-between gap-3">
                <h2 className="font-heading text-base font-semibold text-foreground-950">
                  Ingresos vs. ganancia
                </h2>
                <div className="flex items-center gap-3 text-[11px]">
                  <span className="flex items-center gap-1.5 text-foreground-600">
                    <span className="h-2.5 w-2.5 rounded-full bg-primary-500"></span>
                    Ingresos
                  </span>
                  <span className="flex items-center gap-1.5 text-foreground-600">
                    <span className="h-2.5 w-2.5 rounded-full bg-accent-500"></span>
                    Ganancia
                  </span>
                </div>
              </div>
              <div className="mt-4">
                <ProfitTrendChart buckets={summary?.buckets ?? []} />
              </div>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center justify-between gap-3">
                <h2 className="font-heading text-base font-semibold text-foreground-950">
                  Top productos por ganancia
                </h2>
                <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-semibold text-secondary-800">
                  {summary?.unitsSold ? `${formatQty(summary.unitsSold)} unidades` : "Sin datos"}
                </span>
              </div>
              <div className="mt-4">
                {(summary?.topProducts?.length ?? 0) === 0 ? (
                  <div className="flex h-[300px] flex-col items-center justify-center gap-2 text-center">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                      <i className="ri-bar-chart-2-line text-xl"></i>
                    </span>
                    <p className="text-sm text-foreground-500">Sin ventas en este período.</p>
                  </div>
                ) : (
                  <ProfitTopProductsChart products={summary?.topProducts ?? []} />
                )}
              </div>
            </section>
          </div>

          <section className="flex flex-col rounded-lg border border-background-200 bg-background-100 p-5">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-heading text-base font-semibold text-foreground-950">
                Detalle por producto
              </h2>
              <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-semibold text-secondary-800">
                {summary?.products.length ?? 0} producto(s)
              </span>
            </div>

            {(summary?.products?.length ?? 0) === 0 ? (
              <div className="mt-4 flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-background-300 bg-background-50 px-4 py-12 text-center">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-line-chart-line text-xl"></i>
                </span>
                <p className="text-sm font-medium text-foreground-700">Sin datos de ganancia</p>
                <p className="max-w-sm text-xs text-foreground-500">
                  Cuando registres ventas desde el POS, aquí verás la ganancia por producto.
                </p>
              </div>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[680px] border-collapse">
                  <thead>
                    <tr className="border-b border-background-200 text-left text-[11px] font-semibold uppercase tracking-wide text-foreground-500">
                      <th className="py-2.5 pr-4">Producto</th>
                      <th className="py-2.5 pr-4 text-right">Unidades</th>
                      <th className="py-2.5 pr-4 text-right">Ingresos</th>
                      <th className="py-2.5 pr-4 text-right">Costo</th>
                      <th className="py-2.5 text-right">Ganancia</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200">
                    {summary?.products.map((row) => (
                      <tr key={row.product_id ?? row.name}>
                        <td className="py-3 pr-4 text-sm font-medium text-foreground-900">{row.name}</td>
                        <td className="py-3 pr-4 text-right text-sm text-foreground-700">
                          {formatQty(row.quantity)}
                        </td>
                        <td className="py-3 pr-4 text-right text-sm text-foreground-700">
                          {formatMoney(row.revenue)}
                        </td>
                        <td className="py-3 pr-4 text-right text-sm text-foreground-700">
                          {formatMoney(row.cost)}
                        </td>
                        <td className="py-3 text-right text-sm font-bold text-accent-800">
                          {formatMoney(row.profit)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}